'use client'

import 'slick-carousel/slick/slick-theme.css'
import 'slick-carousel/slick/slick.css'
import './ProductsSliderStyles.css'

import clsx from 'clsx'
import { LazyMotion, m, useInView } from 'framer-motion'
import {
  ComponentProps,
  ComponentRef,
  MouseEvent,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useId } from 'react-aria'
import Slider, { Settings } from 'react-slick'

import ProductCard, { ProductCardProps } from '../../components/ProductCard'
import { useWindowSize } from '../../hooks/useWindowSize'
import { LoadableComponentProps } from '../../types'
import FeatureProducts from '.'

// Mouse movement (px) between press and release above which the gesture counts as a drag, not a click
const DRAG_CLICK_THRESHOLD = 5

const motionFeatures = () => import('ui/helpers/motionFeatures').then(res => res.default)

type ProductsSliderProps = LoadableComponentProps<{
  products: NonNullable<ComponentProps<typeof FeatureProducts>['data']>['products']
  onAddToBasketClick: (variantId: string) => Promise<void>
  onCardClick: () => void
  accessibility?: NonNullable<ProductCardProps['data']>['accessibility']
}>

export default function ProductsSlider({ data, loading }: ProductsSliderProps) {
  const [currentSlide, setCurrentSlide] = useState(0)
  const [isInitialRender, setIsInitialRender] = useState(true)

  const sliderId = useId()

  const sliderRef = useRef<Slider>(null)
  const mouseDownXRef = useRef<number | null>(null)

  // react-slick only suppresses the click after a swipe in the bubble phase, which is too late:
  // the product card link has already navigated. So we block it in the capture phase instead.
  const handleClickCapture = (event: MouseEvent<HTMLDivElement>) => {
    const startX = mouseDownXRef.current
    mouseDownXRef.current = null

    if (startX !== null && Math.abs(event.clientX - startX) > DRAG_CLICK_THRESHOLD) {
      event.preventDefault()
      event.stopPropagation()
    }
  }

  const sliderSettings: Settings = useMemo(
    () => ({
      swipeToSlide: true,
      speed: 500,
      arrows: false,
      slidesToScroll: 1,
      slidesToShow: 1,
      beforeChange: (_, next) => {
        setCurrentSlide(next)
      },
      infinite: false,
      centerPadding: '26px',
      variableWidth: true,
      className: `product-slider${isInitialRender ? ' initial' : ''}`,
      dots: data?.products.length && data?.products.length > 3 ? true : false,
      // @ts-ignore
      appendDots: dots => <DotsContainer dots={dots} />,
      customPaging: index => (
        <Dot
          isActive={currentSlide === index}
          sliderId={sliderId}
          slideNumber={index + 1}
          onClick={() => {
            sliderRef.current?.slickGoTo(index)
          }}
        />
      ),
      responsive: [
        {
          breakpoint: 1023,
          settings: {
            dots: false,
          },
        },
      ],
    }),
    [currentSlide, data?.products.length, isInitialRender, sliderId],
  )

  useLayoutEffect(() => {
    setIsInitialRender(false)
  }, [])

  return (
    <LazyMotion features={motionFeatures} strict>
      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
      <div
        onMouseDownCapture={event => {
          mouseDownXRef.current = event.clientX
        }}
        onClickCapture={handleClickCapture}
      >
        {/* @ts-ignore */}
        <Slider
          ref={sliderRef}
          {...sliderSettings}
          className={clsx(
            'product-slider relative isolate !flex flex-col gap-3 lg:gap-8',
            isInitialRender && ' initial',
          )}
        >
          {loading
            ? new Array(3)
                .fill(null)
                .map((_, index) => <ProductSlide key={`loading-product-card-${index}`} loading />)
            : data.products.map((product, index) => (
                <ProductSlide
                  key={index}
                  data={{
                    product,
                    onAddToBasketClick: data.onAddToBasketClick,
                    onCardClick: data.onCardClick,
                    accessibility: data.accessibility,
                  }}
                />
              ))}
        </Slider>
      </div>
    </LazyMotion>
  )
}

const DotsContainer = ({ dots }: { dots: React.ReactNode }) => {
  return (
    <div className='flex justify-center'>
      <ul className='grid w-[384px] auto-cols-auto grid-flow-col'>{dots}</ul>
    </div>
  )
}

const Dot = ({
  sliderId,
  isActive,
  onClick,
  slideNumber,
}: {
  sliderId: string
  isActive: boolean
  onClick: () => void
  slideNumber: number
}) => {
  return (
    <button
      onClick={onClick}
      aria-label={`Navigate to ${slideNumber} slide`}
      className='w-full py-2'
    >
      <div className='relative h-0.5 bg-ecrudark'>
        {isActive && (
          <m.div
            layoutId={`SliderDot${sliderId}`}
            className='absolute inset-0 z-10 h-0.5 bg-black'
          />
        )}
      </div>
    </button>
  )
}

function ProductSlide({
  data,
  loading,
}: LoadableComponentProps<{
  product: NonNullable<ProductsSliderProps['data']>['products'][number]
  onAddToBasketClick: NonNullable<ProductsSliderProps['data']>['onAddToBasketClick']
  onCardClick: NonNullable<ProductsSliderProps['data']>['onCardClick']
  accessibility?: NonNullable<ProductCardProps['data']>['accessibility']
}>) {
  const slideWrapperRef = useRef<ComponentRef<'div'>>(null)
  const isSlideInView = useInView(slideWrapperRef)
  const { windowSize } = useWindowSize()

  return (
    <div ref={slideWrapperRef}>
      {loading ? (
        <ProductCard
          loading
          imageSizes='(min-width: 768px) 50vw, 100vw'
          className='aspect-[7/8]'
          containerClassName='product-slide-card lg:w-[440px] xl:min-w-[440px]'
          disableDescription
        />
      ) : (
        <ProductCard
          imageSizes='(min-width: 768px) 50vw, 100vw'
          className='aspect-[7/8]'
          containerClassName='product-slide-card lg:w-[440px] xl:min-w-[440px]'
          disableDescription
          shoppingButtonSize={windowSize.width >= 1024 ? 'normal' : 'medium'}
          onAddToBasketClick={data.onAddToBasketClick}
          onCardClick={data.onCardClick}
          disableTabIndex={!isSlideInView}
          data={{
            ...data.product,
            accessibility: { ...data.accessibility, ...data.product.accessability },
          }}
        />
      )}
    </div>
  )
}
